import React from "react";

/**
 * ListItem - A reusable list item component with an image, title, description, and a swappable right-side component.
 * All elements are vertically centered.
 *
 * @param {Object} props
 * @param {string|Object} props.image - Image source or object with src and alt properties
 * @param {string} props.label - Label above the title
 * @param {boolean} props.showLabel - Whether to show the label
 * @param {string} props.title - Main title text
 * @param {string} props.description - Description text
 * @param {React.ReactNode} props.children - External component to render on the right side
 * @param {string} props.width - Width of the list item (default: w-96)
 */
const ListItem = ({
  showImage = true,
  image = "/api/placeholder/48",
  label = "Label",
  showLabel = true,
  title = "List Item",
  showDescription = true,
  description = "Supporting line text lorem ipsum dolor sit amet, consectetur.",
  width = "w-[320px]",
  children, // Swappable right-side component (mounted externally)
}) => {
  const imgSrc = typeof image === "string" ? image : image.src;
  const imgAlt = typeof image === "string" ? "Item image" : image.alt || "Item image";

  return (
    <div className={`${width} px-4 py-3 flex justify-start items-center gap-4 rounded-lg border border-gray-200 bg-white shadow-sm`}>
      {/* Always visible Image */}
      {showImage &&
        <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-purple-600">
          <img src={imgSrc} alt={imgAlt} className="w-full h-full object-cover" />
        </div>
      }

      {/* Middle Content */}
      <div className="flex-1 flex flex-col justify-center items-start gap-0.5 overflow-hidden">
        {showLabel && <div className="text-zinc-700 text-xs font-normal">{label}</div>}
        <div className="text-neutral-900 text-base font-semibold truncate w-full">{title}</div>
        {showDescription && <div className="text-zinc-800 text-sm font-normal">{description}</div>}
      </div>

      {/* Right Side - Externally Mounted Component */}
      {children && <div className="flex-shrink-0 flex justify-center items-center">{children}</div>}
    </div>
  );
};

export default ListItem;